import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Button, Typography, Divider, Spin, message } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { policyDocumentsAPI } from '../services/api';
import { roleLabel } from '../utils/labels';

const { Title, Text, Paragraph } = Typography;

interface PolicyDocument {
  id: number;
  title: string;
  key: string;
  content: string;
  version: string;
  versionDate: string;
}

interface DocumentAcceptanceModalProps {
  visible: boolean;
  role: string;
  onAcceptAll: () => void;
  onCancel: () => void;
}

const DocumentAcceptanceModal: React.FC<DocumentAcceptanceModalProps> = ({
  visible,
  role,
  onAcceptAll,
  onCancel
}) => {
  const [documents, setDocuments] = useState<PolicyDocument[]>([]);
  const [acceptedDocs, setAcceptedDocs] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && role) {
      loadDocuments();
    }
  }, [visible, role]);

  const loadDocuments = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await policyDocumentsAPI.checkAcceptance(role);
      const docs = (response?.documents || []) as PolicyDocument[];
      setDocuments(docs);
      setAcceptedDocs(new Set());

      // Backend confirmed no required docs — allow through
      if (Array.isArray(docs) && docs.length === 0) {
        onAcceptAll();
      }
    } catch (error) {
      setLoadError(true);
      setDocuments([]);
      message.error('Не удалось загрузить документы. Повторите попытку.');
      console.error('Load documents error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (documentId: number, checked: boolean) => {
    const newAccepted = new Set(acceptedDocs);
    if (checked) {
      newAccepted.add(documentId);
    } else {
      newAccepted.delete(documentId);
    }
    setAcceptedDocs(newAccepted);
  };

  const handleAcceptAll = async () => {
    if (loadError) {
      message.warning('Сначала загрузите документы');
      return;
    }
    if (documents.length === 0) {
      onAcceptAll();
      return;
    }
    if (acceptedDocs.size !== documents.length) {
      message.warning('Необходимо принять все документы');
      return;
    }

    setSubmitting(true);
    try {
      await Promise.all(
        documents.map((doc) => policyDocumentsAPI.acceptDocument(doc.id, doc.version))
      );

      message.success('Документы приняты');
      onAcceptAll();
    } catch (error: any) {
      console.error('Accept documents error:', error);
      const errorMessage =
        error?.response?.data?.error ||
        'Ошибка при принятии документов. Попробуйте выйти и войти заново.';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const allAccepted = acceptedDocs.size === documents.length && documents.length > 0;
  const canProceed = !loadError && !loading && (documents.length === 0 || allAccepted);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />
          <span>Принятие документов</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={700}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          Отмена
        </Button>,
        loadError ? (
          <Button key="retry" onClick={loadDocuments} disabled={submitting}>
            Повторить
          </Button>
        ) : null,
        <Button
          key="accept"
          type="primary"
          onClick={handleAcceptAll}
          disabled={!canProceed}
          loading={submitting}
        >
          {documents.length === 0 ? 'Продолжить' : 'Принять все документы'}
        </Button>
      ].filter(Boolean)}
      closable={false}
      maskClosable={false}
      keyboard={false}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Загрузка документов...</div>
        </div>
      ) : loadError ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="danger">Не удалось загрузить документы. Нажмите «Повторить».</Text>
        </div>
      ) : (
        <div>
          <Paragraph>
            Для продолжения работы в режиме <strong>"{roleLabel(role)}"</strong> необходимо
            ознакомиться и принять следующие документы:
          </Paragraph>

          <Divider />

          {documents.map((doc, index) => (
            <div key={doc.id} style={{ marginBottom: 24 }}>
              <Title level={5}>{doc.title}</Title>
              <Text type="secondary">Версия: {doc.version}</Text>

              <div
                style={{
                  maxHeight: 200,
                  overflow: 'auto',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  padding: 12,
                  marginTop: 8,
                  marginBottom: 12,
                  background: '#fafafa'
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: doc.content || 'Содержимое документа не загружено'
                  }}
                />
              </div>

              <Checkbox
                checked={acceptedDocs.has(doc.id)}
                onChange={(e) => handleCheckboxChange(doc.id, e.target.checked)}
              >
                Я ознакомился(лась) и принимаю условия данного документа
              </Checkbox>

              {index < documents.length - 1 && <Divider />}
            </div>
          ))}

          {documents.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Text type="secondary">Нет документов для принятия</Text>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DocumentAcceptanceModal;
